/**
 * DistillationDefenderPanel — arcade VID lane shooter.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAnimationFrame } from "@csk/demo-ui";
import {
  ArcadeActionButton,
  ArcadeArenaCard,
  ArcadeArenaStage,
  ArcadeKeyframes,
  ArcadeLogPanel,
  ArcadeMissionPrompt,
  ArcadePlayfieldFrame,
  ArcadePlayfieldMarker,
  ArcadeStatusDots,
  FlashOverlay,
  arcadeLaneCenter,
  createArcadeHudStripProps,
  pointsForHit,
  rankForScore,
  timeBonus,
  useArcadeActionHotkeys,
  useArcadeClock,
  useReducedMotion,
} from "../../../games/index.js";
import {
  activeDistillationGenerators,
  createDistillationDefenderState,
  DISTILLATION_DEFENDER_LEVELS,
  getDistillationDefenderLevel,
  legalDistillationShots,
  remainingA1Violators,
  stepDistillationDefender,
  stripAllGeneratorsMoveGenerated,
  tickDistillationDefender,
  type DistillationDefenderState,
} from "../algorithms/distillationDefender.js";
import {
  defenderFlashColor,
  GeneratorTile,
  Lane,
} from "./distillation-defender-panel/ArenaPieces.js";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
const ROUND_SECONDS = 55;
const HIT_POINTS = 35;
type FlashTone = "hit" | "miss" | "win" | "escape" | null;
export function DistillationDefenderPanel(): React.JSX.Element {
  const [state, setState] = useState<DistillationDefenderState>(() =>
    createDistillationDefenderState(),
  );
  const [combo, setCombo] = useState(0);
  const [score, setScore] = useState(0);
  const [flash, setFlash] = useState<FlashTone>(null);
  const [flashToken, setFlashToken] = useState(0);
  const clock = useArcadeClock(ROUND_SECONDS);
  const { pause: pauseClock } = clock;
  const reducedMotion = useReducedMotion();
  const level = useMemo(
    () => getDistillationDefenderLevel(state.levelId),
    [state.levelId],
  );
  const activeGenerators = useMemo(
    () => activeDistillationGenerators(state),
    [state],
  );
  const legalShots = useMemo(() => legalDistillationShots(state), [state]);
  const legalTargetId = legalShots[0]?.targetId ?? null;
  const showFlash = useCallback((tone: Exclude<FlashTone, null>): void => {
    setFlash(tone);
    setFlashToken((token) => token + 1);
  }, []);
  useAnimationFrame(
    ({ deltaMs }) => {
      setState((current) => tickDistillationDefender(current, deltaMs));
    },
    { enabled: clock.running && state.status === "playing", maxFps: 60 },
  );
  useEffect(() => {
    if (state.status === "playing") return;
    pauseClock();
    showFlash(state.status === "cleared" ? "win" : "escape");
  }, [pauseClock, showFlash, state.status]);
  const reset = (levelId = state.levelId): void => {
    setState(createDistillationDefenderState(levelId));
    setCombo(0);
    setScore(0);
    setFlash(null);
    setFlashToken(0);
    clock.reset(ROUND_SECONDS);
    clock.start();
  };
  const moveToLane = (lane: number): void => {
    setState((current) =>
      stepDistillationDefender(current, { type: "move", lane }),
    );
  };
  const moveRelative = (delta: -1 | 1): void => {
    moveToLane(state.playerLane + delta);
  };
  const fire = (): void => {
    const before = state;
    const next = stepDistillationDefender(before, { type: "strip" });
    setState(next);
    const stripped = next.strippedIds.length > before.strippedIds.length;
    if (!stripped) {
      setCombo(0);
      showFlash("miss");
      return;
    }
    const nextCombo = combo + 1;
    const bonus = next.status === "cleared" ? timeBonus(clock.remaining) : 0;
    setCombo(nextCombo);
    setScore((value) => value + pointsForHit(nextCombo, HIT_POINTS) + bonus);
    showFlash(next.status === "cleared" ? "win" : "hit");
  };
  const toggleClock = (): void => {
    if (clock.running) clock.pause();
    else if (state.status === "playing") clock.start();
  };
  useArcadeActionHotkeys({
    extraBindings: [
      {
        key: "ArrowLeft",
        enabled: state.status === "playing",
        onTrigger: (): void => {
          moveRelative(-1);
        },
      },
      {
        key: "a",
        enabled: state.status === "playing",
        onTrigger: (): void => {
          moveRelative(-1);
        },
      },
      {
        key: "ArrowRight",
        enabled: state.status === "playing",
        onTrigger: (): void => {
          moveRelative(1);
        },
      },
      {
        key: "d",
        enabled: state.status === "playing",
        onTrigger: (): void => {
          moveRelative(1);
        },
      },
    ],
    primaryEnabled: state.status === "playing",
    onPrimary: fire,
    onReset: reset,
  });
  return (
    <PanelSurface.PanelShell
      title="Distillation Defender"
      subtitle="VID as an arcade lane shooter · strip only A1 violators"
      accentColor={panelTokens.distill}
    >
      <ArcadeKeyframes />

      <PanelSurface.SectionHeading>VID Arena</PanelSurface.SectionHeading>
      <ArcadeArenaCard>
        <ArcadeArenaStage
          hudProps={createArcadeHudStripProps({
            stats: [
              {
                label: "A1 left",
                value: remainingA1Violators(state),
                color: panelTokens.violation,
              },
              {
                label: "stripped",
                value: state.strippedIds.length,
                color: panelTokens.distill,
              },
              {
                label: "immune",
                value: state.preservedIds.length,
                color: panelTokens.immune,
              },
              {
                label: "shots",
                value: state.shots,
                color: panelTokens.carrier,
              },
            ],
            score,
            rank: rankForScore(score),
            rankColor: panelTokens.a4,
            scoreColor: panelTokens.bright,
            borderColor: panelTokens.border,
            clock,
            timerAccent: panelTokens.distill,
            timerDanger: panelTokens.violation,
            timerTrack: "rgba(134,143,192,0.18)",
            combo,
            comboColor: panelTokens.distill,
            comboMuted: panelTokens.muted,
            reducedMotion,
            background: "rgba(0,0,0,0.24)",
          })}
          levelSelectProps={{
            levels: DISTILLATION_DEFENDER_LEVELS,
            activeLevelId: state.levelId,
            onSelect: reset,
            onReset: () => {
              reset();
            },
            accentColor: panelTokens.distill,
            borderColor: panelTokens.border,
            mutedColor: panelTokens.muted,
            brightColor: panelTokens.bright,
            activeBackground: `${panelTokens.distill}22`,
            background: "rgba(5,7,25,0.44)",
          }}
        >
          <ArcadePlayfieldFrame
            borderColor={panelTokens.border}
            background="rgba(5,7,25,0.9)"
            accentColor={panelTokens.distill}
            secondaryColor={panelTokens.violation}
            gridSize={52}
            minHeight={530}
            header={
              <div className="flex flex-wrap items-center justify-between gap-3">
                <PanelSurface.SectionHeading>
                  Distillation lane
                </PanelSurface.SectionHeading>
                <PanelSurface.Badge color={panelTokens.muted}>
                  strip A1 only
                </PanelSurface.Badge>
              </div>
            }
          >
            <div className="mb-4">
              <ArcadeMissionPrompt
                label="projector"
                title={
                  legalTargetId
                    ? "A1 violator in band"
                    : "Aim for an A1 violator"
                }
                detail={state.brief}
                color={
                  legalTargetId ? panelTokens.violation : panelTokens.distill
                }
                hotkey="Space"
                status={level.lanes[state.playerLane] ?? state.status}
              />
            </div>
            <div
              role="application"
              tabIndex={0}
              className="relative h-[390px] min-h-[330px] overflow-hidden rounded-2xl border border-white/10 bg-[#050719] outline-none"
              aria-label="Distillation Defender arena"
            >
              <div
                aria-hidden
                className="absolute left-0 right-0 border-y border-solid bg-cyan-300/5"
                style={{
                  top: `${level.stripZone.start}%`,
                  height: `${level.stripZone.end - level.stripZone.start}%`,
                  borderColor: "rgba(125,211,252,0.38)",
                }}
              />
              {level.lanes.map((lane, index) => (
                <Lane
                  key={lane}
                  lane={lane}
                  index={index}
                  count={level.lanes.length}
                  active={index === state.playerLane}
                  onSelect={moveToLane}
                />
              ))}
              <ArcadePlayfieldMarker
                ariaHidden
                x={arcadeLaneCenter(state.playerLane, level.lanes.length)}
                bottom={20}
                anchor="bottom"
                className="h-[86px] w-[4px] rounded-full bg-cyan-200/60 shadow-[0_0_26px_rgba(125,211,252,0.55)]"
              />
              <ArcadePlayfieldMarker
                as="button"
                x={arcadeLaneCenter(state.playerLane, level.lanes.length)}
                bottom={12}
                anchor="bottom"
                onClick={fire}
                className="h-[44px] w-[72px] rounded-lg border-2 border-solid text-[11px] font-black"
                borderColor={
                  legalTargetId ? panelTokens.violation : panelTokens.carrier
                }
                color={legalTargetId ? "#ffe4e6" : "#dbeafe"}
                background={
                  legalTargetId
                    ? "rgba(244,63,94,0.28)"
                    : "rgba(14,165,233,0.16)"
                }
                boxShadow={
                  legalTargetId
                    ? "0 0 28px rgba(244,63,94,0.45)"
                    : "0 0 18px rgba(14,165,233,0.25)"
                }
              >
                FIRE
              </ArcadePlayfieldMarker>
              {activeGenerators.map((generator) => (
                <GeneratorTile
                  key={generator.id}
                  generator={generator}
                  laneCount={level.lanes.length}
                  legal={generator.id === legalTargetId}
                />
              ))}
              {flash && (
                <FlashOverlay
                  token={flashToken}
                  color={defenderFlashColor(flash)}
                  reducedMotion={reducedMotion}
                />
              )}
            </div>
          </ArcadePlayfieldFrame>

          <ArcadeLogPanel
            title="VID log"
            event={state.lastEvent}
            borderColor={panelTokens.border}
            background="rgba(5,7,25,0.68)"
            textColor={panelTokens.text}
            badges={[
              PanelSurface.badge("VID", panelTokens.distill),
              PanelSurface.badge("A1 only", panelTokens.violation),
            ]}
          >
            <div className="space-y-3">
              <ArcadeStatusDots
                items={[
                  { label: "A1 in band", active: legalTargetId !== null },
                  {
                    label: "immune core",
                    active: state.status === "cleared",
                  },
                  {
                    label: "strip-all absent",
                    active: !stripAllGeneratorsMoveGenerated(),
                  },
                ]}
                activeColor={panelTokens.distill}
                inactiveColor={panelTokens.muted}
              />
              <div className="grid grid-cols-3 gap-2">
                <ArcadeActionButton
                  color={panelTokens.carrier}
                  onClick={() => {
                    moveRelative(-1);
                  }}
                  ariaLabel="Move projector left"
                  icon="<"
                >
                  Left
                </ArcadeActionButton>
                <ArcadeActionButton
                  color={
                    legalTargetId ? panelTokens.violation : panelTokens.border
                  }
                  onClick={fire}
                  ariaLabel="Fire VID projector"
                  icon=">"
                >
                  Fire
                </ArcadeActionButton>
                <ArcadeActionButton
                  color={panelTokens.carrier}
                  onClick={() => {
                    moveRelative(1);
                  }}
                  ariaLabel="Move projector right"
                  icon=">"
                >
                  Right
                </ArcadeActionButton>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <ArcadeActionButton
                  color={
                    clock.running ? panelTokens.border : panelTokens.distill
                  }
                  onClick={toggleClock}
                  ariaLabel={clock.running ? "Pause timer" : "Run timer"}
                  icon={clock.running ? "||" : ">"}
                >
                  {clock.running ? "Pause" : "Run"}
                </ArcadeActionButton>
                <ArcadeActionButton
                  color={panelTokens.violation}
                  onClick={() => {
                    reset();
                  }}
                  ariaLabel="Reset Distillation Defender"
                  icon="R"
                >
                  Reset
                </ArcadeActionButton>
              </div>
            </div>
          </ArcadeLogPanel>
        </ArcadeArenaStage>
      </ArcadeArenaCard>
    </PanelSurface.PanelShell>
  );
}
